/**
 * Formats a time string from `HH:MM:SS` (or `HH:MM`) format to `HH.MM`.
 *
 * @param time - A time string such as `"10:00:00"` or `"19:30"`, or `null`/`undefined`.
 * @returns The formatted time string (e.g. `"10.00"`), or an empty string if the input is invalid or missing.
 *
 * @example
 * formatTime("10:00:00") // "10.00"
 * formatTime("19:30")    // "19.30"
 * formatTime(null)       // ""
 */

import type { Locale } from '@/i18n/locales';

export function formatTime(time: string | null | undefined) {
  const [hours, minutes] = time ? time.split(':') : [];

  if (hours && minutes) {
    return `${hours}.${minutes}`;
  }

  return '';
}

/**
 * Formats a date into YYYY-MM-DD format.
 * @param date
 * @returns A string representing the date in YYYY-MM-DD format.
 *
 * @example
 * formatDate(new Date(2023, 0, 1)) // "2023-01-01"
 */
export function formatDate(date: Date) {
  const YYYY = date.getFullYear();
  const MM = (date.getMonth() + 1).toString().padStart(2, '0');
  const DD = date.getDate().toString().padStart(2, '0');

  return `${YYYY}-${MM}-${DD}`;
}

/**
 * Formats a date to show day and month in numeric format for a specific locale.
 * @param date - A Date object, timestamp (number), or date string.
 * @param locale - The locale to use for formatting.
 * @returns A localized string with day and month in numeric format.
 *
 * @example
 * // Sweden (sv-SE) uses D/M format
 * formatDateShort(new Date(2023, 7, 26), 'sv-SE') // "26/8"
 *
 * // Norway (nb-NO) uses D.M format
 * formatDateShort(new Date(2023, 7, 26), 'nb-NO') // "26.8"
 *
 * // Finland (fi-FI) uses D.M format
 * formatDateShort(new Date(2023, 7, 26), 'fi-FI') // "26.8"
 */
export function formatDateShort(date: Date | number | string, locale: Locale) {
  const d = new Date(date);

  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'numeric',
    timeZone: typeof date === 'string' ? 'UTC' : undefined,
  });
}
